<template>

  <div class="bulk-editor-grid">
     <!-- Toolbar -->
    <div class="grid-toolbar">
      <div class="d-flex align-items-center grid-toolbar-main">
         <b-dropdown
          size="sm"
          variant="outline-secondary"
          class="grid-column-picker mr-3"
          no-caret
          > <template #button-content> <i class="fas fa-columns mr-1"></i>Hiển thị </template>
          <b-dropdown-form
            class="p-2"
            style="min-width: 200px"
            > <b-form-checkbox
              v-for="col in toggleableColumns"
              :key="col.key"
              :checked="visibleColumns.includes(col.key)"
              @change="toggleColumn(col.key, $event)"
              class="mb-2"
              > {{ col.label }} </b-form-checkbox
            > </b-dropdown-form
          > </b-dropdown
        >
        <div class="grid-hint text-muted small">
           <span class="mr-3"><i class="fas fa-user text-info"></i> Cá nhân</span> <span
            class="mr-3"
            ><i class="fas fa-building text-primary"></i> Doanh nghiệp</span
          >
        </div>

      </div>
       <!-- Validation summary -->
      <div class="grid-validation-summary">

        <div
          v-if="validationSummary.valid > 0"
          class="summary-item text-success"
          title="Hợp lệ"
        >
           <i class="fas fa-check-circle"></i> <b>{{ validationSummary.valid }}</b
          >
        </div>

        <div
          v-if="validationSummary.missing > 0"
          class="summary-item text-warning"
          title="Thiếu thông tin"
        >
           <i class="fas fa-exclamation-triangle"></i> <b>{{ validationSummary.missing }}</b
          >
        </div>

        <div
          v-if="validationSummary.invalid > 0"
          class="summary-item text-danger"
          title="Dữ liệu lỗi"
        >
           <i class="fas fa-times-circle"></i> <b>{{ validationSummary.invalid }}</b
          >
        </div>

      </div>

    </div>
     <!-- Grid table -->
    <div
      class="grid-scroll-area"
      ref="scrollArea"
    >

      <table
        class="grid-table"
        role="grid"
      >

        <thead>

          <tr>

            <th class="grid-th grid-th--index">#</th>
             <!-- Fixed Columns -->
            <th
              class="grid-th"
              style="width: 100px"
            >
               Mã HĐ
            </th>

            <th
              class="grid-th"
              style="width: 140px"
            >
               Khách hàng
            </th>

            <th
              class="grid-th text-center"
              style="width: 90px"
            >
               Loại
            </th>
             <!-- Dynamic Columns -->
            <th
              v-for="col in activeDynamicColumns"
              :key="col.key"
              class="grid-th"
              :style="{ minWidth: col.width, width: col.width }"
            >
               {{ col.label }}
            </th>

            <th class="grid-th grid-th--status">TT</th>

          </tr>

        </thead>

        <tbody>

          <tr
            v-for="(row, idx) in rows"
            :key="row.billId"
            class="grid-row"
            :class="{
              'grid-row--enterprise': row.buyerType === 'ENTERPRISE',
              'grid-row--invalid': rowValidation[idx] && rowValidation[idx].status === 'INVALID',
              'grid-row--missing': rowValidation[idx] && rowValidation[idx].status === 'MISSING',
            }"
          >
             <!-- Index -->
            <td class="grid-td grid-td--index">{{ idx + 1 }}</td>
             <!-- Bill Code -->
            <td class="grid-td grid-td--readonly">
               <span
                class="text-dark font-weight-bold"
                :title="row.billCode"
                >{{ row.billCode }}</span
              >
            </td>
             <!-- Customer Name -->
            <td class="grid-td grid-td--readonly">

              <div
                class="text-truncate"
                :title="row.customerName"
                style="max-width: 130px"
              >
                 {{ row.customerName || '—' }}
              </div>

            </td>
             <!-- Buyer Type Toggle -->
            <td class="grid-td text-center">

              <div class="type-toggle type-toggle--3">

                <div
                  class="type-option"
                  :class="{ active: row.buyerType === 'INDIVIDUAL' }"
                  @click="setBuyerType(row, 'INDIVIDUAL')"
                  title="Cá nhân"
                >
                   <i class="fas fa-user"></i>
                </div>

                <div
                  class="type-option"
                  :class="{ active: row.buyerType === 'ENTERPRISE' || !row.buyerType }"
                  @click="setBuyerType(row, 'ENTERPRISE')"
                  title="Doanh nghiệp"
                >
                   <i class="fas fa-building"></i>
                </div>
              </div>

            </td>
             <!-- Dynamic Fields -->
            <td
              v-for="col in activeDynamicColumns"
              :key="col.key"
              class="grid-td"
              :class="{
                'grid-td--error': hasError(idx, col.key),
                'grid-td--disabled': !isColumnVisibleForRow(col, row),
              }"
            >
              <!-- Skip rendering if column not applicable for this row's buyer type -->
              <template v-if="!isColumnVisibleForRow(col, row)">
                <span class="text-muted small pl-2">—</span>
              </template>

              <!-- Tax Code Special Handling (ENTERPRISE only) -->
              <div
                v-else-if="col.key === 'buyerTaxCode'"
                class="d-flex position-relative"
              >
                <input
                  :ref="`cell-${row.billId}-${col.key}`"
                  v-model="row[col.key]"
                  class="grid-input pr-4"
                  :class="{ 'is-loading': lookupLoading[row.billId] }"
                  placeholder="Nhập MST"
                  @keydown.enter.prevent="lookupTaxCode(row)"
                  @blur="onCellBlur(row)"
                />

                <!-- Search Button inside input -->
                <button
                  class="btn-icon-search"
                  tabindex="-1"
                  :disabled="!row.buyerTaxCode"
                  title="Tra cứu thông tin doanh nghiệp"
                  @click="lookupTaxCode(row)"
                >
                  <i v-if="lookupLoading[row.billId]" class="fas fa-spinner fa-spin text-primary"></i>
                  <i v-else class="fas fa-search text-muted hover-primary"></i>
                </button>
              </div>

              <div v-else-if="col.key === 'buyerAddress'" class="cell-address-wrapper">
                <textarea
                  :ref="`cell-${row.billId}-${col.key}`"
                  v-model="row[col.key]"
                  class="grid-input grid-textarea"
                  rows="1"
                  placeholder="Địa chỉ..."
                  @blur="onCellBlur(row)"
                  @keydown.enter.prevent
                ></textarea>
              </div>
              <!-- Default Input -->
              <input
                v-else
                :ref="`cell-${row.billId}-${col.key}`"
                v-model="row[col.key]"
                class="grid-input"
                :placeholder="col.placeholder"
                @blur="onCellBlur(row)"
              />

              <!-- Tooltip for Error -->
              <b-tooltip
                v-if="hasError(idx, col.key)"
                :target="`cell-${row.billId}-${col.key}`"
                placement="top"
                triggers="hover focus"
                variant="danger"
              >
                {{ rowValidation[idx].errors[col.key] }}
              </b-tooltip>
            </td>
             <!-- Row Status -->
            <td class="grid-td grid-td--status">
               <i
                v-if="rowValidation[idx]"
                :class="statusIcon(rowValidation[idx].status)"
                :title="statusLabel(rowValidation[idx].status)"
                class="status-icon"
              ></i
              >
            </td>

          </tr>

        </tbody>

      </table>

    </div>

  </div>

</template>

<script>
  import {
    validateGridRow,
    validateGrid,
    ROW_STATUS,
    BUYER_TYPE,
    syncRetailRowDraftFromActiveFields,
    switchRetailRowBuyerType,
    isRetailFacetBackedRow,
  } from '@/view/pages/einvoice/utils/bulkBuyer.utils'

  // Define dynamic columns with buyer type visibility rules
  // forTypes: which buyer types show this column (empty = all types)
  const DYNAMIC_COLUMNS = [
    {
      key: 'buyerTaxCode',
      label: 'Mã số thuế',
      width: '140px',
      alwaysVisible: true,
      placeholder: 'MST',
      forTypes: ['ENTERPRISE'],
    },
    {
      key: 'buyerName',
      label: 'Tên',
      width: '180px',
      alwaysVisible: true,
      placeholder: 'Họ tên / Đơn vị',
      forTypes: [],
    },
    {
      key: 'buyerAddress',
      label: 'Địa chỉ',
      width: '200px',
      alwaysVisible: true,
      placeholder: 'Địa chỉ',
      forTypes: ['ENTERPRISE', 'INDIVIDUAL'],
    },
    {
      key: 'buyerEmail',
      label: 'Email (nhiều email cách nhau dấu phẩy)',
      width: '220px',
      alwaysVisible: true,
      placeholder: 'email1@abc.com, email2@abc.com',
      forTypes: [],
    },
    {
      key: 'buyerPhone',
      label: 'SĐT',
      width: '120px',
      alwaysVisible: false,
      placeholder: 'Số điện thoại',
      forTypes: [],
    },
  ]

  export default {
    name: 'BulkEditorGrid',
    props: {
      value: { type: Array, required: true },
    },
    data() {
      return {
        visibleColumns: DYNAMIC_COLUMNS.filter((c) => c.alwaysVisible).map((c) => c.key),
        lookupLoading: {}, // { billId: boolean }
      }
    },
    computed: {
      rows() {
        return this.value
      },
      toggleableColumns() {
        return DYNAMIC_COLUMNS.filter((c) => !c.alwaysVisible)
      },
      activeDynamicColumns() {
        return DYNAMIC_COLUMNS.filter((c) => c.alwaysVisible || this.visibleColumns.includes(c.key))
      },
      rowValidation() {
        return this.rows.map((r) => validateGridRow(r))
      },
      validationSummary() {
        return validateGrid(this.rows).summary
      },
    },
    methods: {
      // ── Interaction ──────────────────────────────
      toggleColumn(key, checked) {
        if (checked) this.visibleColumns.push(key)
        else this.visibleColumns = this.visibleColumns.filter((k) => k !== key)
      },

      setBuyerType(row, type) {
        const nextRow = isRetailFacetBackedRow(row)
          ? switchRetailRowBuyerType(row, type)
          : { ...row, buyerType: type }

        this.replaceRow(nextRow)
        // Auto-focus relevant field based on buyer type
        this.$nextTick(() => {
          if (type === BUYER_TYPE.ENTERPRISE) {
            this.focusCell(nextRow.billId, 'buyerTaxCode')
          } else {
            this.focusCell(nextRow.billId, 'buyerName')
          }
        })
      },

      /**
       * Check if a column should be visible/editable for a specific row's buyer type.
       * @param {Object} col - Column definition
       * @param {Object} row - Row data
       * @returns {boolean}
       */
      isColumnVisibleForRow(col, row) {
        // If forTypes is empty array, column is visible for all types
        if (!col.forTypes || col.forTypes.length === 0) {
          return true
        }
        const rowType = row.buyerType || BUYER_TYPE.ENTERPRISE
        return col.forTypes.includes(rowType)
      },

      focusCell(billId, key) {
        const ref = this.$refs[`cell-${billId}-${key}`]
        const el = Array.isArray(ref) ? ref[0] : ref
        if (el && el.focus) el.focus()
      },

      onCellBlur(row) {
        const nextRow = isRetailFacetBackedRow(row) ? syncRetailRowDraftFromActiveFields(row) : row
        this.replaceRow(nextRow)
      },

      hasError(idx, key) {
        return this.rowValidation[idx] && this.rowValidation[idx].errors[key]
      },

      // ── Tax Code Lookup ──────────────────────────
      async lookupTaxCode(row) {
        if (!row.buyerTaxCode || row.buyerType !== 'ENTERPRISE') return

        const taxCode = (row.buyerTaxCode || '').trim()
        this.$set(this.lookupLoading, row.billId, true)
        try {
          const http = this.$http || this.axios || window.axios
          if (!http) {
            console.error('HTTP client not found')
            return
          }

          // Use new endpoint: /einvoice/companies
          const res = await http.get('v1/einvoice/companies', { params: { taxCode } })
          const company = res.data?.data

          // Not found: { data: null }
          if (!company) {
            this.$bvToast.toast('Không tìm thấy thông tin doanh nghiệp', {
              title: 'Tra cứu thất bại',
              variant: 'warning',
              solid: true,
              autoHideDelay: 2000,
            })
            return
          }

          // Found: { data: { taxCode, name, address, ... } }
          row.buyerName = company.name || ''
          row.buyerAddress = company.address || ''
          const nextRow = isRetailFacetBackedRow(row) ? syncRetailRowDraftFromActiveFields(row) : row

          this.$bvToast.toast(`Đã tìm thấy: ${row.buyerName}`, {
            title: 'Tra cứu thành công',
            variant: 'success',
            solid: true,
            autoHideDelay: 2000,
          })
          this.replaceRow(nextRow)
        } catch (e) {
          console.error('Tax lookup error:', e)
          this.$bvToast.toast('Lỗi khi tra cứu mã số thuế', {
            title: 'Lỗi',
            variant: 'danger',
            solid: true,
            autoHideDelay: 2000,
          })
        } finally {
          this.$set(this.lookupLoading, row.billId, false)
          if (!isRetailFacetBackedRow(row)) {
            this.$emit('input', [...this.rows])
          }
        }
      },

      replaceRow(nextRow) {
        const nextRows = this.rows.map((currentRow) =>
          currentRow.billId === nextRow.billId ? nextRow : currentRow,
        )
        this.$emit('input', nextRows)
        return nextRows
      },

      // ── Utils ────────────────────────────────────
      statusIcon(status) {
        if (status === ROW_STATUS.VALID) return 'fas fa-check-circle text-success'
        if (status === ROW_STATUS.MISSING) return 'fas fa-exclamation-circle text-warning'
        return 'fas fa-times-circle text-danger'
      },
      statusLabel(status) {
        if (status === ROW_STATUS.VALID) return 'Hợp lệ'
        if (status === ROW_STATUS.MISSING) return 'Thiếu thông tin'
        return 'Dữ liệu lỗi'
      },

      /** Public: Reveal column if needed (for parent validation links) */
      revealAndFocusCell(billId, key) {
        if (!this.visibleColumns.includes(key)) {
          this.visibleColumns.push(key)
        }
        this.$nextTick(() => this.focusCell(billId, key))
      },
    },
  }
</script>

<style lang="scss" scoped>
  $border-color: #ebedf3;
$bg-light: #f3f6f9;
$primary: #3699ff;

.bulk-editor-grid {
  border: 1px solid $border-color;
  border-radius: 6px;
  background: #fff;
  display: flex;
  flex-direction: column;
}

.grid-toolbar {
  padding: 8px 12px;
  background: $bg-light;
  border-bottom: 1px solid $border-color;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.grid-toolbar-main {
  flex-wrap: wrap;
  gap: 8px;
}

.grid-validation-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 0.85rem;
}

.grid-scroll-area {
  overflow: auto;
  max-height: 450px;
}

.grid-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  
  th {
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 10;
    padding: 8px 10px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    color: #b5b5c3;
    border-bottom: 2px solid $border-color;
    white-space: nowrap;
    box-shadow: 0 2px 2px -1px rgba(0,0,0,0.05);
  }

  td {
    padding: 6px 8px;
    border-bottom: 1px solid $border-color;
    vertical-align: middle;
    background: #fff;
  }
  
  .grid-row:hover td {
    background: #f8f9fa;
  }

  .grid-row--enterprise td {
    background: #fafffd; // Slight green tint for enterprise
  }

  // .grid-row--foreign td {
  //   background: #fffcf5; // Slight orange/warm tint for foreign - DISABLED
  // }
}

// ── Inputs ─────────────────────────
.grid-input {
  width: 100%;
  border: 1px solid transparent;
  padding: 6px 8px;
  font-size: 0.85rem;
  border-radius: 4px;
  background: transparent;
  transition: all 0.2s;
  
  &:hover {
    background: #fff;
    border-color: #e4e6ef;
  }
  
  &:focus {
    background: #fff;
    border-color: $primary;
    box-shadow: 0 0 0 2px rgba($primary, 0.1);
    outline: none;
  }
  
  &.grid-textarea {
    resize: none;
    line-height: 1.4;
    height: 34px; // Single line height
    transition: border-color 0.2s;

    &:focus {
      height: auto;
      min-height: 80px;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 100;
      width: 100%;
      min-width: 250px;
      box-shadow: 0 4px 15px rgba(0,0,0,0.15);
      background: #fff;
    }
  }
}

.cell-address-wrapper {
  position: relative;
  height: 34px;
  width: 100%;
}

.grid-td--error .grid-input {
   background: #fff5f8;
   border-color: #f64e60;
}

// ── Type Toggle ────────────────────
.type-toggle {
  display: flex;
  background: #ebedf3;
  padding: 2px;
  border-radius: 4px;
  width: fit-content;
  margin: 0 auto;

  &.type-toggle--3 {
    // Slightly narrower padding for 3 options
    .type-option {
      padding: 4px 6px;
    }
  }

  .type-option {
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 3px;
    color: #b5b5c3;
    transition: all 0.2s;
    font-size: 0.85rem;

    &:hover { color: $primary; }

    &.active {
      background: #fff;
      color: $primary;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);

      // Color variations for different types
      i.fa-building { color: $primary; }
      i.fa-user { color: #17a2b8; }
      i.fa-globe { color: #f6a609; }
    }
  }
}

// ── Select Input ──────────────────
.grid-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3e%3cpath fill='%23666' d='M2 0L0 2h4zm0 5L0 3h4z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 8px 10px;
  padding-right: 24px;
  cursor: pointer;
}

// ── Disabled Cell ─────────────────
.grid-td--disabled {
  background: #f8f9fa !important;
  cursor: not-allowed;
}

// ── Search Button ──────────────────
.btn-icon-search {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  z-index: 5;
  
  &:disabled { cursor: not-allowed; opacity: 0.5; }
  .hover-primary:hover { color: $primary !important; }
}

.status-icon {
  font-size: 1.1rem;
}

@media (max-width: 767.98px) {
  .grid-toolbar {
    align-items: flex-start;
  }

  .grid-validation-summary {
    width: 100%;
  }

  .grid-column-picker {
    margin-right: 0 !important;
  }
}
</style>
